/**
 * /hr/slack — Slack connector settings page.
 *
 * Wired to live endpoints:
 *   GET  /api/v1/hr/slack/config     — workspace metadata + event routing
 *   PATCH /api/v1/hr/slack/config/routings — save routing table
 *   POST /api/v1/hr/slack/test       — send test DM to the HR user's account
 *   POST /api/v1/hr/slack/disconnect — audit-log the disconnect action
 *   GET  /api/v1/hr/slack/activity   — last 20 Slack audit_log rows
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/primitives/Card'
import { PageHead } from '../components/primitives/PageHead'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { ApiError } from '../api/client'
import {
  disconnectSlack,
  getSlackConfig,
  getSlackActivity,
  updateSlackRoutings,
  sendSlackTestDm,
  type SlackEventKey,
  type SlackEventRouting,
  type SlackWorkspaceConfig,
} from '../api/hr-slack'

// ── Static metadata ──────────────────────────────────────────────────────────

const EVENT_META: Record<SlackEventKey, { name: string; desc: string }> = {
  'leave.submitted': {
    name: 'Leave submitted',
    desc: "A new request landed in someone's queue",
  },
  'leave.approved': {
    name: 'Leave approved',
    desc: 'Decision delivered to the employee',
  },
  'leave.rejected': {
    name: 'Leave rejected',
    desc: 'Includes reason text in the DM',
  },
  'leave.escalated': {
    name: 'Leave escalated to Director',
    desc: 'Blackout or unpaid leave routing',
  },
  'leave.cancelled': {
    name: 'HR cancellation',
    desc: 'When HR overrides an approved leave',
  },
}

const EVENT_KEY_ORDER: ReadonlyArray<SlackEventKey> = [
  'leave.submitted',
  'leave.approved',
  'leave.rejected',
  'leave.escalated',
  'leave.cancelled',
]

/** Fallback routing table shown when the API endpoint doesn't exist yet. */
function defaultRoutings(): SlackEventRouting[] {
  return EVENT_KEY_ORDER.map((key) => ({
    key,
    channel: key === 'leave.submitted' || key === 'leave.cancelled' ? '#hr-leave-feed' : key === 'leave.escalated' ? '#hr-escalations' : '',
    dmActor: true,
    active: true,
  }))
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function isNotFoundError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 503)
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function HrSlackPage(): JSX.Element {
  return (
    <HrAccessGuard>
      <div className="page">
        <SlackPageInner />
      </div>
    </HrAccessGuard>
  )
}

function SlackPageInner(): JSX.Element {
  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['hr-slack-config'],
    queryFn: getSlackConfig,
    retry: false,
  })

  const activityQuery = useQuery({
    queryKey: ['hr-slack-activity'],
    queryFn: getSlackActivity,
    retry: false,
  })

  // Local draft for event routing — seeded from API, or from defaults when the
  // endpoint doesn't exist yet.
  const apiRoutings: SlackEventRouting[] | undefined = configQuery.data?.eventRoutings
  const [routings, setRoutings] = useState<SlackEventRouting[] | null>(null)

  // Lazily initialise local draft once API data arrives (or defaults when 404).
  const effectiveRoutings: SlackEventRouting[] =
    routings ?? apiRoutings ?? defaultRoutings()

  function patchRouting(key: SlackEventKey, patch: Partial<SlackEventRouting>): void {
    setRoutings(
      effectiveRoutings.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    )
  }

  const saveRoutingsMutation = useMutation({
    mutationFn: () =>
      updateSlackRoutings({ routings: effectiveRoutings }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr-slack-config'] })
    },
  })

  const testDmMutation = useMutation({
    mutationFn: (event?: SlackEventKey) => sendSlackTestDm(event),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr-slack-activity'] })
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectSlack(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr-slack-config'] })
    },
  })

  const config: SlackWorkspaceConfig | undefined = configQuery.data
  const apiUnavailable = isNotFoundError(configQuery.error)

  // Connection badge
  const connected = config?.connected ?? false

  return (
    <>
      <PageHead
        eyebrow="HR · Integrations"
        title="Slack connector"
        subtitle="How Lumen and ILMU talk to your Slack workspace. § 14.1"
      />

      {/* ── Connection card ───────────────────────────────────────── */}
      <Card padding={0} style={{ marginBottom: 16 }}>
        <div
          style={{
            padding: 22,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            gap: 18,
            alignItems: 'center',
          }}
        >
          {/* Slack brand icon placeholder — 56×56 rounded square */}
          <div
            aria-hidden="true"
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: '#4A154B',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <svg
              width={28}
              height={28}
              viewBox="0 0 122.8 122.8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-label="Slack"
            >
              {/* Simplified Slack hashtag shape in white */}
              <rect x="25" y="50" width="20" height="45" rx="10" fill="white" opacity="0.9" />
              <rect x="77" y="27" width="20" height="45" rx="10" fill="white" opacity="0.9" />
              <rect x="50" y="77" width="45" height="20" rx="10" fill="white" opacity="0.9" />
              <rect x="27" y="25" width="45" height="20" rx="10" fill="white" opacity="0.9" />
            </svg>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 17, fontWeight: 600 }}>
                {config?.workspaceDomain ?? '—'}
              </span>
              {apiUnavailable ? (
                <ConnectionBadge connected={false} label="Not configured" />
              ) : connected ? (
                <ConnectionBadge connected label="Connected" />
              ) : (
                <ConnectionBadge connected={false} label="Disconnected" />
              )}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--text-3)',
                marginTop: 4,
              }}
            >
              <span>
                {config?.connectedByName && config?.connectedAt
                  ? `Connected by ${config.connectedByName} on ${config.connectedAt}`
                  : '—'}
              </span>
              {' · Bot user: '}
              <span style={{ fontFamily: 'var(--font-mono)' }}>
                {config?.botUsername ?? '—'}
              </span>
              {' · Workspace ID '}
              <span style={{ fontFamily: 'var(--font-mono)' }}>
                {config?.workspaceId ?? '—'}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => testDmMutation.mutate(undefined)}
                disabled={apiUnavailable || !connected || testDmMutation.isPending}
                title={
                  apiUnavailable
                    ? 'Slack not configured'
                    : !connected
                      ? 'Connect Slack first'
                      : 'Send a test DM to your account'
                }
                style={buttonStyleSoft}
              >
                {testDmMutation.isPending && testDmMutation.variables === undefined
                  ? 'Sending…'
                  : testDmMutation.isSuccess && testDmMutation.variables === undefined
                    ? 'Sent ✓'
                    : 'Send test DM'}
              </button>
              <button
                type="button"
                disabled={apiUnavailable || disconnectMutation.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      'Disconnect this Slack workspace? HR will no longer receive leave notifications via Slack until reconnected.',
                    )
                  ) {
                    disconnectMutation.mutate()
                  }
                }}
                style={connected ? buttonStyleDanger : buttonStyleSoft}
                title={apiUnavailable ? 'Slack not configured' : undefined}
              >
                {disconnectMutation.isPending
                  ? 'Disconnecting…'
                  : disconnectMutation.isSuccess
                    ? 'Disconnected ✓'
                    : connected
                      ? 'Disconnect'
                      : 'Reconnect'}
              </button>
            </div>
            {testDmMutation.isSuccess && testDmMutation.variables === undefined ? (
              <div style={{ fontSize: 11.5, color: 'var(--success)', textAlign: 'right' }}>
                {testDmMutation.data?.detail ?? 'Test DM delivered to your Slack account.'}
              </div>
            ) : null}
            {testDmMutation.isError ? (
              <div style={{ fontSize: 11.5, color: 'var(--danger)', textAlign: 'right' }}>
                {testDmMutation.error instanceof Error
                  ? testDmMutation.error.message
                  : 'Failed to send test DM.'}
              </div>
            ) : null}
            {disconnectMutation.isError ? (
              <div style={{ fontSize: 11.5, color: 'var(--danger)', textAlign: 'right' }}>
                {disconnectMutation.error instanceof Error
                  ? disconnectMutation.error.message
                  : 'Failed to record disconnect.'}
              </div>
            ) : null}
          </div>
        </div>

        {/* Token scopes strip */}
        <div
          style={{
            padding: '12px 22px',
            borderTop: '1px solid var(--border)',
            background: 'var(--surface-2)',
            display: 'flex',
            gap: 16,
            fontSize: 12,
            color: 'var(--text-2)',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--success)',
                flexShrink: 0,
              }}
              aria-hidden="true"
            />
            Bot Token scoped to:{' '}
            <span style={{ fontFamily: 'var(--font-mono)', marginLeft: 4 }}>
              {config?.tokenScopes && config.tokenScopes.length > 0
                ? config.tokenScopes.join(', ')
                : '—'}
            </span>
          </span>
          <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            Last token rotation:{' '}
            <span style={{ fontFamily: 'var(--font-mono)' }}>
              {config?.lastTokenRotation ?? '—'}
            </span>
          </span>
        </div>
      </Card>

      {/* ── Event routing table ───────────────────────────────────── */}
      <Card padding={0} style={{ marginBottom: 16 }}>
        <div
          style={{
            padding: '18px 22px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Event routing</div>
            <div style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 2 }}>
              For each event, choose where Lumen sends notifications
            </div>
          </div>
          {routings !== null && (
            <button
              type="button"
              disabled={saveRoutingsMutation.isPending}
              onClick={() => saveRoutingsMutation.mutate()}
              style={buttonStylePrimary}
            >
              {saveRoutingsMutation.isPending ? 'Saving…' : 'Save routing'}
            </button>
          )}
        </div>

        {saveRoutingsMutation.isError && (
          <div
            role="alert"
            style={{
              margin: '0 22px 12px',
              padding: '8px 12px',
              borderRadius: 8,
              background: 'var(--danger-tint)',
              color: 'var(--danger)',
              fontSize: 13,
            }}
          >
            {saveRoutingsMutation.error instanceof ApiError
              ? saveRoutingsMutation.error.message
              : 'Save failed — POST /api/v1/hr/slack/config/routings not yet implemented'}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table
            data-testid="slack-routing-table"
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}
          >
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                <Th>Event</Th>
                <Th style={{ width: 200 }}>Channel (audit)</Th>
                <Th style={{ width: 120 }}>DM the actor</Th>
                <Th style={{ width: 90 }}>Active</Th>
                <Th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {effectiveRoutings
                .filter((routing) => routing.key in EVENT_META)
                .map((routing) => {
                const meta = EVENT_META[routing.key]
                return (
                  <tr
                    key={routing.key}
                    data-testid="routing-row"
                    style={{ borderTop: '1px solid var(--border)' }}
                  >
                    <Td>
                      <div style={{ fontWeight: 500, fontSize: 13.5 }}>
                        {meta.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: 'var(--text-3)',
                          marginTop: 2,
                        }}
                      >
                        {meta.desc}
                      </div>
                      <div
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          color: 'var(--text-3)',
                          marginTop: 4,
                        }}
                      >
                        {routing.key}
                      </div>
                    </Td>
                    <Td>
                      <input
                        type="text"
                        aria-label={`Channel for ${meta.name}`}
                        value={routing.channel}
                        placeholder="#channel or blank"
                        onChange={(e) =>
                          patchRouting(routing.key, { channel: e.target.value })
                        }
                        style={channelInputStyle}
                      />
                    </Td>
                    <Td>
                      <ToggleButton
                        on={routing.dmActor}
                        onLabel="DM on"
                        offLabel="DM off"
                        onClick={() =>
                          patchRouting(routing.key, { dmActor: !routing.dmActor })
                        }
                      />
                    </Td>
                    <Td>
                      <ToggleButton
                        on={routing.active}
                        onLabel="On"
                        offLabel="Off"
                        onClick={() =>
                          patchRouting(routing.key, { active: !routing.active })
                        }
                      />
                    </Td>
                    <Td>
                      <button
                        type="button"
                        disabled={apiUnavailable || !connected || testDmMutation.isPending}
                        onClick={() => testDmMutation.mutate(routing.key)}
                        title={
                          apiUnavailable
                            ? 'Slack not configured'
                            : !connected
                              ? 'Connect Slack first'
                              : `Send a ${meta.name} preview to Slack`
                        }
                        style={buttonStyleSoft}
                        aria-label={`Send a ${meta.name} preview to Slack`}
                      >
                        {testDmMutation.isPending &&
                        testDmMutation.variables === routing.key
                          ? 'Sending…'
                          : testDmMutation.isSuccess &&
                              testDmMutation.variables === routing.key
                            ? 'Sent ✓'
                            : 'Test'}
                      </button>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Bottom two-column grid ────────────────────────────────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 16,
        }}
      >
        {/* Recent activity */}
        <Card padding={0}>
          <div style={{ padding: '18px 22px' }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Recent activity</div>
          </div>

          {activityQuery.isLoading && (
            <div
              style={{
                padding: '24px 22px',
                fontSize: 13,
                color: 'var(--text-3)',
              }}
            >
              Loading…
            </div>
          )}

          {activityQuery.isError && (
            <div
              role="alert"
              style={{
                padding: '16px 22px',
                fontSize: 13,
                color: 'var(--danger)',
              }}
            >
              {activityQuery.error instanceof Error
                ? activityQuery.error.message
                : 'Failed to load recent activity.'}
            </div>
          )}

          {activityQuery.data && activityQuery.data.rows.length === 0 && (
            <div
              style={{
                padding: '24px 22px',
                fontSize: 13,
                color: 'var(--text-3)',
              }}
            >
              No recent activity.
            </div>
          )}

          {activityQuery.data && activityQuery.data.rows.length > 0 && (
            <div>
              {activityQuery.data.rows.map((row, i) => (
                <ActivityRow key={i} row={row} />
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface ConnectionBadgeProps {
  connected: boolean
  label: string
}

function ConnectionBadge({ connected, label }: ConnectionBadgeProps): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: connected ? 'var(--success-tint)' : 'var(--danger-tint)',
        color: connected ? 'var(--success)' : 'var(--danger)',
      }}
    >
      {connected && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--success)',
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
      )}
      {label}
    </span>
  )
}

interface ToggleButtonProps {
  on: boolean
  onLabel: string
  offLabel: string
  onClick: () => void
}

function ToggleButton({ on, onLabel, offLabel, onClick }: ToggleButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={on ? buttonStyleActive : buttonStyleSoft}
    >
      {on ? onLabel : offLabel}
    </button>
  )
}

interface ActivityRowData {
  when: string
  eventKey: string
  actorName: string
  target: string
}

function ActivityRow({ row }: { row: ActivityRowData }): JSX.Element {
  return (
    <div
      style={{
        padding: '10px 18px',
        borderBottom: '1px solid var(--border)',
        fontSize: 12.5,
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-3)',
            fontSize: 11,
          }}
        >
          {row.when}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            padding: '1px 6px',
            borderRadius: 4,
            background: 'var(--surface-2)',
            color: 'var(--text-2)',
          }}
        >
          {row.eventKey}
        </span>
      </div>
      <div style={{ color: 'var(--text-2)', fontSize: 12 }}>
        {'→ '}
        {row.target}
        {' · routed to '}
        <strong style={{ color: 'var(--text)' }}>{row.actorName}</strong>
      </div>
    </div>
  )
}

// ── Table cell helpers ────────────────────────────────────────────────────────

function Th({
  children,
  style,
}: {
  children?: React.ReactNode
  style?: React.CSSProperties
}): JSX.Element {
  return (
    <th
      style={{
        padding: '10px 16px',
        textAlign: 'left',
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--text-2)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        ...style,
      }}
    >
      {children}
    </th>
  )
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}): JSX.Element {
  return (
    <td style={{ padding: '12px 16px', color: 'var(--text)', ...style }}>
      {children}
    </td>
  )
}

// ── Style constants ───────────────────────────────────────────────────────────

const buttonStylePrimary: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--steel-blue)',
  color: 'var(--surface)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
}

const buttonStyleSoft: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text-2)',
  fontWeight: 500,
  fontSize: 13,
  cursor: 'pointer',
}

const buttonStyleActive: React.CSSProperties = {
  ...buttonStyleSoft,
  background: 'var(--steel-blue)',
  borderColor: 'var(--steel-blue)',
  color: 'var(--surface)',
}

const buttonStyleDanger: React.CSSProperties = {
  padding: '7px 14px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--danger)',
  color: 'var(--surface)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
}

const channelInputStyle: React.CSSProperties = {
  padding: '7px 10px',
  fontSize: 12.5,
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
}
